import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { RootState } from '../redux/store';
import { API_BASE_URL } from '../helpers/common';
import { IPaginationResponse } from '../interfaces/responces/IPagination.response';

// Structural Pallete (from palletes module)
export interface IPallete {
  id: number;
  companyId: number;
  warehouseId?: number;
  externalId?: number;
  code: string;
  rfidTag?: string;
  name: string;
  palleteType?: string;  // 'EUR' | 'US' | 'standard' | 'box'
  status?: string;       // 'available' | 'occupied' | 'reserved' | 'maintenance' | 'damaged'
  location?: string;
  capacity?: number;
  currentLoad?: number;
  isActive: boolean;
  lastUsedAt?: string;
  registeredAt?: string;
  meta?: any;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;
  warehouse?: {
    id: number;
    code?: string;
    name?: string;
  };
  warehouse_id?: number; // legacy field
}

// Stock Inventory Item (pallete with product data for orders)
export interface IPalleteStockInventory {
  id: number;
  companyId?: number;
  stockOwnerId?: number;
  warehouseId?: number;
  productId?: number;
  palletId?: number;
  paleteCode?: string;
  paleteName?: string;
  productCode?: string;
  productName?: string;
  productShortDescription?: string;
  productLongDescription?: string;
  qtyPieces?: number;
  qtyCartons?: number;
  paleteRegisteredAt?: string;
  paleteUnit?: string;
  unit?: string;
  barcode?: string;
  insertDate?: string;
  daysInStock?: number;
  batchNumber?: string;
  expirationDate?: string;
  quantity?: number; // For order flow
  [key: string]: any;
}

export interface IPalleteFilter {
  page?: number;
  query?: string;
  limit?: number;
  order?: string | string[];
  orderDirection?: 'ASC' | 'DESC';
  filterId?: string;
  stockOwnerId?: number;
}

export interface IPalleteResponse {
  list: IPallete[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export const palletesApi = createApi({
  reducerPath: 'palletesApi',
  tagTypes: ['Pallete'],
  refetchOnMountOrArgChange: true,
  baseQuery: fetchBaseQuery({
    baseUrl: API_BASE_URL,
    prepareHeaders: async (headers, { getState }) => {
      const token = (getState() as RootState).user.info.accessToken;
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  endpoints: (build) => ({
    // ==========================================
    // Structural Palletes (Pallete Module)
    // ==========================================
    getPalletesList: build.query<IPalleteResponse, IPalleteFilter>({
      transformResponse: (response: { data: IPalleteResponse }) =>
        response.data,
      query: (params) => ({
        url: '/palletes/list',
        method: 'GET',
        params: {
          page: params.page,
          search: params.query,
          limit: params.limit,
          order: params.order,
          orderDirection: params.orderDirection,
          filterId: params.filterId,
          ...(params.stockOwnerId ? { stockOwnerId: params.stockOwnerId } : {}),
        },
      }),
      providesTags: () => ['Pallete'],
    }),
    getPalleteById: build.query<IPallete, { id: number }>({
      transformResponse: (response: { data: IPallete }) => response.data,
      query: ({ id }) => ({
        url: `/palletes/${id}`,
        method: 'GET',
      }),
      providesTags: () => ['Pallete'],
    }),
    createPallete: build.mutation<IPallete, Partial<IPallete>>({
      transformResponse: (response: { data: IPallete }) => response.data,
      query: (newPallete) => ({
        url: '/palletes/create',
        method: 'POST',
        body: newPallete,
      }),
      invalidatesTags: ['Pallete'],
    }),
    updatePallete: build.mutation<IPallete, { pallete: Partial<IPallete>; id: number }>({
      transformResponse: (response: { data: IPallete }) => response.data,
      query: ({ pallete, id }) => ({
        url: `/palletes/${id}`,
        method: 'PUT',
        body: pallete,
      }),
      invalidatesTags: ['Pallete'],
    }),
    deletePallete: build.mutation<void, number>({
      query: (id) => ({
        url: `/palletes/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Pallete'],
    }),
  }),
});

export const {
  useGetPalletesListQuery,
  useLazyGetPalletesListQuery,
  useGetPalleteByIdQuery,
  useLazyGetPalleteByIdQuery,
  useCreatePalleteMutation,
  useUpdatePalleteMutation,
  useDeletePalleteMutation,
} = palletesApi;

